import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { createBaby, updateBaby } from "../../../api/baby";

const MIN_TICKET_SEARCH_LENGTH = 3;

const emptyForm = {
  mother_ticket: null,
  mother_patient: null,
  baby_name: "",
  date_of_birth: "",
  time_of_birth: "",
  gender: "",
  birth_weight: "",
  blood_group: "",
  delivery_type: "",
  birth_type: "single",
  baby_condition: "",
  responsible_doctor: null,
  responsible_nurse: null,
  admission_date: "",
  current_status: "registered",
  notes: "",
};

const emptyPaymentForm = {
  total_amount: "",
  discount: "",
  payment_notes: "",
};

const mapTicketOption = (ticket) => ({
  id: ticket.id,
  label: ticket.ticket_number || `Ticket ${ticket.id}`,
  ticket_number: ticket.ticket_number || "",
  patient_id: ticket.patient,
  patient_code: ticket.patient_mrn_display || "",
  patient_display_id: ticket.patient_name_display ? ticket.patient_mrn_display || "" : "",
  patient_name: ticket.patient_name_display || "",
  phone: ticket.patient_phone_display || "",
});

const getUniquePatientOptions = (tickets = []) => {
  const seenPatients = new Set();

  return tickets
    .map(mapTicketOption)
    .filter((option) => {
      if (!option.patient_id || seenPatients.has(option.patient_id)) {
        return false;
      }
      seenPatients.add(option.patient_id);
      return true;
    });
};

const getTicketOptionLabel = (option) => {
  if (!option) return "";
  const patientName = option.patient_name || "";
  const patientCode = option.patient_display_id || option.patient_code || "";
  const ticketNumber = option.ticket_number || option.label || "";
  return [patientName, patientCode ? `(${patientCode})` : "", ticketNumber]
    .filter(Boolean)
    .join(" ");
};

const mapUserOption = (user) => ({
  id: user.id,
  label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username,
});

export default function BabyForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token;
  const currentUser = auth?.user?.user;
  const { t } = useTranslation();

  const genderOptions = [
    { value: 1, label: t("baby.list.male", "Male") },
    { value: 2, label: t("baby.list.female", "Female") },
  ];

  const deliveryTypeOptions = [
    { value: "normal_delivery", label: t("baby.form.deliveryTypeOptions.normal_delivery", "Normal Delivery") },
    { value: "caesarean_delivery", label: t("baby.form.deliveryTypeOptions.caesarean_delivery", "Caesarean Delivery") },
  ];

  const birthTypeOptions = [
    { value: "single", label: t("baby.form.birthTypeOptions.single", "Single") },
    { value: "twin", label: t("baby.form.birthTypeOptions.twin", "Twin") },
    { value: "triplet", label: t("baby.form.birthTypeOptions.triplet", "Triplet") },
    { value: "multiple", label: t("baby.form.birthTypeOptions.multiple", "Multiple") },
  ];

  const conditionOptions = [
    { value: "stable", label: t("baby.form.conditionOptions.stable", "Stable") },
    { value: "critical", label: t("baby.form.conditionOptions.critical", "Critical") },
    { value: "premature", label: t("baby.form.conditionOptions.premature", "Premature") },
    { value: "low_birth_weight", label: t("baby.form.conditionOptions.low_birth_weight", "Low Birth Weight") },
  ];

  const statusOptions = [
    { value: "registered", label: t("baby.form.statusOptions.registered", "Registered") },
    { value: "admitted", label: t("baby.form.statusOptions.admitted", "Admitted") },
    { value: "under_observation", label: t("baby.form.statusOptions.under_observation", "Under Observation") },
    { value: "discharged", label: t("baby.form.statusOptions.discharged", "Discharged") },
    { value: "referred", label: t("baby.form.statusOptions.referred", "Referred") },
    { value: "deceased", label: t("baby.form.statusOptions.deceased", "Deceased") },
  ];

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [doctorSearch, setDoctorSearch] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const normalizedTicketSearch = ticketSearch.trim();
  const shouldFetchTickets = normalizedTicketSearch.length >= MIN_TICKET_SEARCH_LENGTH;
  const paymentSubTotal = Number(paymentForm.total_amount || 0);
  const paymentDiscount = Number(paymentForm.discount || 0);
  const paymentGrandTotal = Math.max(paymentSubTotal - paymentDiscount, 0);

  const { data: babyData, isLoading: babyLoading } = useFetchObjects(
    ["baby", id],
    id ? `babies/${id}/` : "",
    token,
    { enabled: !!id && !!token }
  );

  const { data: ticketsData, isLoading: ticketsLoading } = useFetchObjects(
    ["baby-tickets", ticketSearch],
    shouldFetchTickets
      ? `tickets/?page_size=10&search=${encodeURIComponent(normalizedTicketSearch)}`
      : "",
    token,
    { enabled: shouldFetchTickets }
  );

  const { data: doctorsData, isLoading: doctorsLoading } = useFetchObjects(
    ["baby-doctors", doctorSearch],
    `users/?page_size=50&doctor=true&search=${encodeURIComponent(doctorSearch)}`,
    token
  );

  const { data: bloodTypesData } = useFetchObjects(
    ["blood-types"],
    "blood-types/?page_size=100",
    token
  );

  const ticketOptions = useMemo(
    () => (shouldFetchTickets ? getUniquePatientOptions(ticketsData?.results || []) : []),
    [shouldFetchTickets, ticketsData]
  );
  const doctorOptions = useMemo(
    () => (doctorsData?.results || []).map(mapUserOption),
    [doctorsData]
  );
  const currentUserDoctorOption = useMemo(() => {
    if (!currentUser?.id) {
      return null;
    }
    if (!(currentUser?.permissions?.doctor || currentUser?.isAdmin)) {
      return null;
    }
    return {
      id: currentUser.id,
      label:
        `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() ||
        currentUser.username ||
        t("baby.form.currentUser", "Current User"),
    };
  }, [currentUser, t]);

  useEffect(() => {
    if (!isEdit || !babyData) {
      return;
    }
    setForm({
      mother_ticket:
        babyData.mother_patient
          ? {
              id: `patient-${babyData.mother_patient}`,
              label: babyData.mother_name || t("baby.form.selectedMother", "Selected Mother"),
              patient_id: babyData.mother_patient,
              patient_code: babyData.mother_patient_id || "",
              patient_display_id: babyData.mother_patient_id || "",
              patient_name: babyData.mother_name || "",
              phone: babyData.mother_phone_number || "",
            }
          : null,
      mother_patient: babyData.mother_patient
        ? {
            id: babyData.mother_patient,
            label: babyData.mother_name || t("baby.form.selectedMother", "Selected Mother"),
            patient_id: babyData.mother_patient_id || "",
            phone: babyData.mother_phone_number || "",
          }
        : null,
      baby_name: babyData.baby_name || "",
      date_of_birth: babyData.date_of_birth || "",
      time_of_birth: babyData.time_of_birth || "",
      gender: babyData.gender ?? "",
      birth_weight: babyData.birth_weight || "",
      blood_group: babyData.blood_group || "",
      delivery_type: babyData.delivery_type || "",
      birth_type: babyData.birth_type || "single",
      baby_condition: babyData.baby_condition || "",
      responsible_doctor: babyData.responsible_doctor
        ? { id: babyData.responsible_doctor, label: babyData.responsible_doctor_name || "Doctor" }
        : null,
      admission_date: babyData.admission_date || "",
      current_status: babyData.current_status || "registered",
      notes: babyData.notes || "",
    });
  }, [babyData, isEdit]);

  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (!form.responsible_doctor && doctorOptions.length > 0) {
      setForm((prev) => ({
        ...prev,
        responsible_doctor: doctorOptions[0],
      }));
    }
    if (!form.responsible_doctor && currentUserDoctorOption) {
      setForm((prev) => ({
        ...prev,
        responsible_doctor: currentUserDoctorOption,
      }));
    }
  }, [currentUserDoctorOption, doctorOptions, form.responsible_doctor, isEdit]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.mother_patient?.id) nextErrors.mother_patient = t("baby.form.validation.motherPatientRequired", "Mother patient is required.");
    if (!form.baby_name.trim()) nextErrors.baby_name = t("baby.form.validation.babyNameRequired", "Baby name is required.");
    if (!form.date_of_birth) nextErrors.date_of_birth = t("baby.form.validation.dateOfBirthRequired", "Date of birth is required.");
    if (form.date_of_birth && form.date_of_birth > new Date().toISOString().slice(0, 10)) {
      nextErrors.date_of_birth = t("baby.form.validation.dateOfBirthFuture", "Date of birth cannot be in the future.");
    }
    if (!form.gender) nextErrors.gender = t("baby.form.validation.genderRequired", "Gender is required.");
    if (!form.birth_weight || Number(form.birth_weight) <= 0) {
      nextErrors.birth_weight = t("baby.form.validation.birthWeightPositive", "Birth weight must be greater than zero.");
    }
    if (!form.delivery_type) nextErrors.delivery_type = t("baby.form.validation.deliveryTypeRequired", "Delivery type is required.");
    if (!form.baby_condition) nextErrors.baby_condition = t("baby.form.validation.babyConditionRequired", "Baby condition is required.");
    if (!form.admission_date) nextErrors.admission_date = t("baby.form.validation.admissionDateRequired", "Admission date is required.");
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveBaby = async () => {
    setSubmitting(true);
    const resolvedDoctorId =
      form.responsible_doctor?.id ||
      currentUserDoctorOption?.id ||
      doctorOptions[0]?.id ||
      null;

    const payload = {
      mother_patient: form.mother_patient.id,
      mother_ticket: form.mother_ticket?.id || null,
      baby_name: form.baby_name,
      date_of_birth: form.date_of_birth,
      time_of_birth: form.time_of_birth || null,
      gender: form.gender,
      birth_weight: form.birth_weight,
      blood_group: form.blood_group || null,
      delivery_type: form.delivery_type,
      birth_type: form.birth_type,
      baby_condition: form.baby_condition,
      responsible_doctor: resolvedDoctorId,
      admission_date: form.admission_date,
      current_status: form.current_status,
      notes: form.notes,
      ...(isEdit
        ? {}
        : {
            payment_total_amount: paymentForm.total_amount || 0,
            payment_discount: paymentForm.discount || 0,
            payment_notes: paymentForm.payment_notes || "",
          }),
    };

    try {
      const response = isEdit
        ? await updateBaby(id, payload, token)
        : await createBaby(payload, token);
      queryClient.invalidateQueries({ queryKey: ["babies"] });
      toast.success(
        isEdit
          ? t("baby.form.success.update", "Baby information updated successfully.")
          : t("baby.form.success.register", "Baby registered successfully.")
      );
      navigate(`/baby/${response.id}`);
      return true;
    } catch (error) {
      const backendErrors = error.response?.data || {};
      setErrors(backendErrors);
      if (backendErrors.detail) {
        toast.error(backendErrors.detail);
      } else {
        toast.error(t("baby.form.error.save", "Unable to save baby information."));
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validateForm()) {
      return;
    }

    if (isEdit) {
      await saveBaby();
      return;
    }

    setPaymentDialogOpen(true);
  };

  const handleConfirmPaymentAndRegister = async () => {
    if (submitting) {
      return;
    }
    if (!paymentForm.total_amount || Number(paymentForm.total_amount) <= 0) {
      toast.error(t("baby.form.paymentTotalRequired", "Total amount is required."));
      return;
    }
    const saved = await saveBaby();
    if (saved) {
      setPaymentDialogOpen(false);
      setPaymentForm(emptyPaymentForm);
    }
  };

  if (isEdit && babyLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEdit ? t("baby.form.title.edit", "Edit Baby") : t("baby.form.title.register", "Register Baby")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t("baby.form.subtitle", "Keep the registration simple and complete for mother, baby, and staff details.")}
            </Typography>
          </Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/baby/list")}>
            {t("baby.form.backToList", "Back to Baby List")}
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t("baby.form.info", "Baby ID is generated automatically when the record is saved.")}
        </Alert>

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            {t("baby.form.motherInfo", "Mother Information")}
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={ticketOptions}
                value={form.mother_ticket}
                inputValue={ticketSearch}
                loading={ticketsLoading}
                openOnFocus={false}
                onChange={(event, value) => {
                  handleChange("mother_ticket", value);
                  handleChange(
                    "mother_patient",
                    value
                      ? {
                          id: value.patient_id,
                          label: value.patient_name || value.label,
                          patient_id: value.patient_display_id || value.patient_code || "",
                          phone: value.phone || "",
                        }
                      : null
                  );
                }}
                onInputChange={(event, value) => setTicketSearch(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={getTicketOptionLabel}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1" fontWeight={700}>
                        {option.patient_name || t("baby.form.noMotherName", "No mother name")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.patient_display_id || option.patient_code || t("baby.form.noPatientId", "No patient ID")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.ticket_number || option.label}
                        {option.phone ? ` • ${option.phone}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("baby.form.searchMotherTicket", "Search Mother By Ticket")}
                    placeholder={t("baby.form.searchMotherTicketPlaceholder", "Type at least 3 characters, e.g. 000")}
                    error={Boolean(errors.mother_patient)}
                    helperText={errors.mother_patient}
                  />
                )}
                noOptionsText={
                  normalizedTicketSearch.length < MIN_TICKET_SEARCH_LENGTH
                    ? t("baby.form.searchMinimum", "Type at least 3 characters to search")
                    : t("baby.form.noResults", "No matching patient found")
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label={t("baby.form.motherPatientId", "Mother Patient ID")} value={form.mother_patient?.patient_id || ""} disabled />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label={t("baby.form.motherName", "Mother Name")} value={form.mother_patient?.label || ""} disabled />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth label={t("baby.form.motherPhoneNumber", "Mother Phone Number")} value={form.mother_patient?.phone || ""} disabled />
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={700} mb={2}>
            {t("baby.form.babyInfo", "Baby Information")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("baby.form.babyName", "Baby Name")}
                value={form.baby_name}
                onChange={(e) => handleChange("baby_name", e.target.value)}
                error={Boolean(errors.baby_name)}
                helperText={errors.baby_name}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label={t("baby.form.dateOfBirth", "Date of Birth")}
                InputLabelProps={{ shrink: true }}
                value={form.date_of_birth}
                onChange={(e) => handleChange("date_of_birth", e.target.value)}
                error={Boolean(errors.date_of_birth)}
                helperText={errors.date_of_birth}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label={t("baby.form.timeOfBirth", "Time of Birth")}
                InputLabelProps={{ shrink: true }}
                value={form.time_of_birth}
                onChange={(e) => handleChange("time_of_birth", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.gender", "Gender")}
                value={form.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
                error={Boolean(errors.gender)}
                helperText={errors.gender}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("baby.form.birthWeight", "Birth Weight")}
                value={form.birth_weight}
                onChange={(e) => handleChange("birth_weight", e.target.value)}
                error={Boolean(errors.birth_weight)}
                helperText={errors.birth_weight}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.bloodGroup", "Blood Group")}
                value={form.blood_group}
                onChange={(e) => handleChange("blood_group", e.target.value)}
              >
                {(bloodTypesData?.results || bloodTypesData || []).map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.deliveryType", "Delivery Type")}
                value={form.delivery_type}
                onChange={(e) => handleChange("delivery_type", e.target.value)}
                error={Boolean(errors.delivery_type)}
                helperText={errors.delivery_type}
              >
                {deliveryTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.birthType", "Birth Type")}
                value={form.birth_type}
                onChange={(e) => handleChange("birth_type", e.target.value)}
              >
                {birthTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.babyCondition", "Baby Condition")}
                value={form.baby_condition}
                onChange={(e) => handleChange("baby_condition", e.target.value)}
                error={Boolean(errors.baby_condition)}
                helperText={errors.baby_condition}
              >
                {conditionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                fullWidth
                label={t("baby.form.admissionDate", "Admission Date")}
                InputLabelProps={{ shrink: true }}
                value={form.admission_date}
                onChange={(e) => handleChange("admission_date", e.target.value)}
                error={Boolean(errors.admission_date)}
                helperText={errors.admission_date}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("baby.form.currentStatus", "Current Status")}
                value={form.current_status}
                onChange={(e) => handleChange("current_status", e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={12} />
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label={t("baby.form.notes", "Notes")}
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                error={Boolean(errors.notes)}
                helperText={errors.notes}
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={4} flexWrap="wrap">
            <Button variant="outlined" onClick={() => navigate("/baby/list")}>
              {t("baby.form.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={submitting ? <CircularProgress color="inherit" size={18} /> : <Save />}
              disabled={submitting}
            >
              {submitting ? t("baby.form.save", "Saving...") : isEdit ? t("baby.form.update", "Update Baby") : t("baby.form.submit", "Register Baby")}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={paymentDialogOpen}
        onClose={() => !submitting && setPaymentDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("baby.form.paymentTitle", "Baby Payment")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">
              {t("baby.form.paymentSubtitle", "Review payment details before completing baby registration.")}
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("baby.form.paymentTotalAmount", "Total Amount")}
                  value={paymentForm.total_amount}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, total_amount: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("baby.form.paymentDiscount", "Discount")}
                  value={paymentForm.discount}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, discount: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("baby.form.paymentNotes", "Notes")}
                  value={paymentForm.payment_notes}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_notes: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa" }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t("baby.form.paymentSubTotal", "Sub Total")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {paymentSubTotal || 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t("baby.form.paymentDiscount", "Discount")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {paymentDiscount || 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1" fontWeight={700}>
                        {t("baby.form.paymentTotalAmount", "Total Amount")}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {paymentGrandTotal}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={submitting}>
            {t("baby.form.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPaymentAndRegister}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress color="inherit" size={18} /> : <Save />}
          >
            {submitting ? t("baby.form.save", "Saving...") : t("baby.form.confirmPayment", "Confirm And Register")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
